import React, { useState, useEffect, useRef } from "react";
import GradientText from "@/components/GradientText";

export default function NumberCounter() {
  const [counters, setCounters] = useState({
    studentsplaced: 0,
    bookings: 0,
    highlyrated: 0,
    community: 0,
  });
  const [hasStarted, setHasStarted] = useState(false);
  const componentRef = useRef(null);

  const targetValues = {
    studentsplaced: 500,
    bookings: 1000,
    highlyrated: 4.5,
    community: 70000,
  }

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasStarted) {
          setHasStarted(true);
        }
      },
      {
        threshold: 0.3, // Start animation when 30% of component is visible
      }
    );

    if (componentRef.current) {
      observer.observe(componentRef.current);
    }

    return () => {
      if (componentRef.current) {
        observer.unobserve(componentRef.current);
      }
    };
  }, [hasStarted]);

  useEffect(() => {
    if (!hasStarted) return;

    const duration = 2000;
    const startTime = Date.now();

    const animateCounters = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easeOut = 1 - Math.pow(1 - progress, 3);

      setCounters({
        studentsplaced: Math.floor(targetValues.studentsplaced * easeOut),
        bookings: Math.floor(targetValues.bookings * easeOut),
        highlyrated: parseFloat(
          (targetValues.highlyrated * easeOut).toFixed(1)
        ),
        community: Math.floor(targetValues.community * easeOut),
      });

      if (progress < 1) {
        requestAnimationFrame(animateCounters);
      }
    };

    requestAnimationFrame(animateCounters);
  }, [hasStarted]);

  const formatNumber = (num, label) => {
    if (label === "Highly Rated") {
      return `${num}/5`;
    }

    if (num >= 1000000) {
      return (num / 1000000).toFixed(1).replace(".0", "") + "M+";
    } else if (num >= 1000) {
      return Math.floor(num / 1000) + "K+";
    } else if (num >= 100) {
      return num + "+";
    }
    return num.toString();
  };

  const StatCard = ({ count, label, icon }) => (
    <div
      className="rounded-2xl p-[2px] shadow-xl relative group h-full"
      style={{
        background:
          "linear-gradient(135deg, rgba(255,255,255,0.7), rgba(0,0,0,0.6))",
      }}
    >
      <div
        className="bg-black rounded-2xl p-6 text-center w-full h-full flex flex-col items-center justify-center
               transition-transform duration-300 group-hover:scale-[1.05] 
               shadow-[inset_0_8px_20px_rgba(255,255,255,0.05),0_8px_30px_rgba(0,0,0,0.4)]"
      >
        <div className="text-white text-3xl font-bold mb-2 drop-shadow-lg">
          {formatNumber(count, label)}
        </div>
        <div className="flex items-center justify-center gap-2 text-gray-300 text-sm">
          <GradientText
            colors={["#49AED9", "#6C42EB", "#9ACBDF", "#49AED9", "#6C42EB"]}
            animationSpeed={3}
            showBorder={false}
            className="custom-class"
          >
            {label}
          </GradientText>{" "}
          {icon}
        </div>
      </div>
    </div>
  );

  return (
    <div ref={componentRef} className="py-16 px-6 relative overflow-hidden">
      {/* Background with dots + gradient overlay - copied from PlacementWhatYoullGet */}
      <div
        className="absolute inset-0 z-0"
        style={{
          backgroundImage: "radial-gradient(#404040 1px, transparent 1px)",
          backgroundSize: "20px 20px",
        }}
      />
      <div
        className="absolute inset-0 z-0"
        style={{
          background:
            "radial-gradient(ellipse at center, transparent 20%, black 70%)",
        }}
      />

      {/* Top and bottom fade gradients */}
      <div
        className="absolute top-0 left-0 right-0 h-24 z-[1]"
        style={{
          background: "linear-gradient(to bottom, black, transparent)",
        }}
      />
      <div
        className="absolute bottom-0 left-0 right-0 h-16 z-[1]"
        style={{
          background: "linear-gradient(to top, black, transparent)",
        }}
      />

      {/* Content sits on top of background */}
      <div className="max-w-6xl mx-auto relative z-10">
        <h2 className="text-white text-2xl md:text-3xl lg:text-4xl font-bold text-center mb-16">
          Numbers That Speak For Themselves
        </h2>

        {/* Responsive Grid: 2x2 on mobile, 4 across on desktop */}
        {/* Responsive Grid: 2x2 on mobile, 4 across on desktop */}
        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-8 items-stretch">
          <StatCard count={counters.studentsplaced} label="Students Placed" />

          <StatCard count={counters.bookings} label="Session Booked" />

          <StatCard count={counters.highlyrated} label="Highly Rated" />

          <StatCard
            count={counters.community}
            label="Community"
            icon={
              <div className="w-5 h-5 bg-blue-600 rounded text-white text-xs flex items-center justify-center font-bold">
                in
              </div>
            }
          />
        </div>
      </div>
    </div>
  );
}
